import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { useGetRegionsQuery } from "@/shared/api/common";
import type { RegionResponse } from "@/shared/api/common";
import { getFamilyStatusLabel, type Locale } from "@/shared/utils";
import { getNationalityLabel } from "@/features/hr/hiring";

interface Props {
  previousValues: Record<string, unknown>;
  requestedChanges: Record<string, unknown>;
  changedSections: string[];
  uploadedFilesUrls?: Record<string, string | null>;
}

const SECTION_LABELS: Record<string, string> = {
  general: "personalSheet.sections.general",
  contacts: "personalSheet.sections.contacts",
  addresses: "personalSheet.sections.addresses",
  emergency_contacts: "personalSheet.sections.emergency",
  id_documents: "personalSheet.sections.idDocuments",
  banking: "personalSheet.sections.banking",
  kyc_documents: "personalSheet.sections.general",
  education: "personalSheet.sections.education",
  experience: "personalSheet.sections.experience",
  social_categories: "personalSheet.sections.socialCategories",
};

const FIELD_LABELS: Record<string, string> = {
  surname: "personalSheet.fields.surname",
  name: "personalSheet.fields.name",
  father_name: "personalSheet.fields.fatherName",
  family_status: "personalSheet.fields.familyStatus",
  gender: "personalSheet.fields.gender",
  nationality: "personalSheet.fields.nationality",
  citizenship: "personalSheet.fields.citizenship",
  is_resident: "personalSheet.fields.isResident",
  is_student: "personalSheet.fields.isStudent",
  city_of_birth_id: "personalSheet.fields.cityOfBirth",
  city_of_birth_other: "personalSheet.fields.cityOfBirth",
  email: "personalSheet.fields.email",
  phone: "personalSheet.fields.phone",
  phone_additional: "personalSheet.fields.phoneAdditional",
  address_registration: "personalSheet.fields.addressRegistration",
  address_factual: "personalSheet.fields.addressFactual",
  contact_name: "personalSheet.fields.contactName",
  contact_phone: "personalSheet.fields.contactPhone",
  contact_relation: "personalSheet.fields.contactRelation",
  national_id_number: "personalSheet.fields.nationalIdNumber",
  national_id_issue_date: "personalSheet.fields.nationalIdIssueDate",
  national_id_expiry_date: "personalSheet.fields.nationalIdExpiryDate",
  national_id_issued_by: "personalSheet.fields.nationalIdIssuedBy",
  bank_name: "personalSheet.fields.bankName",
  iban_account: "personalSheet.fields.ibanAccount",
  bik_number: "personalSheet.fields.bikNumber",
  degree: "personalSheet.fields.degree",
  university_name: "personalSheet.fields.universityName",
  specialty: "personalSheet.fields.specialty",
  graduation_year: "personalSheet.fields.graduationYear",
  diploma_number: "personalSheet.fields.diplomaNumber",
  organization_name: "personalSheet.fields.organizationName",
  speciality: "personalSheet.fields.specialty",
  work_position: "personalSheet.fields.workPosition",
  work_format: "personalSheet.fields.workFormat",
  beginning_date: "personalSheet.fields.beginningDate",
  end_date: "personalSheet.fields.endDate",
  category_type: "personalSheet.fields.categoryType",
  issue_date: "personalSheet.fields.issueDate",
  expiry_date: "personalSheet.fields.expiryDate",
  notes: "personalSheet.fields.notes",
};

function resolveRegionName(id: unknown, regions: RegionResponse[], locale: Locale): string {
  if (typeof id !== "number") return String(id);
  const region = regions.find((r) => r.id === id);
  if (!region) return String(id);
  return locale === "kk" ? region.name_kk : region.name_ru;
}

function formatValue(
  value: unknown,
  key: string,
  t: TFunction,
  tCommon: (key: string) => string,
  tApply: (key: string) => string,
  gender: string,
  regions: RegionResponse[] = [],
  locale: Locale = "ru",
): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "boolean")
    return value ? t("personalSheet.booleanValues.yes") : t("personalSheet.booleanValues.no");
  if (key === "family_status") return getFamilyStatusLabel(value as string, gender, tCommon);
  if (key === "nationality") return getNationalityLabel(value as string, gender, tApply);
  if (key === "gender") return t(`personalSheet.genderValues.${value}`) || String(value);
  if (key === "work_format") return t(`personalSheet.workFormatValues.${value}`) || String(value);
  if (key === "degree") return t(`personalSheet.degreeValues.${value}`) || String(value);
  if (key === "contact_relation") return t(`personalSheet.relationValues.${value}`) || String(value);
  if (key === "category_type") return t(`personalSheet.socialCategoryValues.${value}`) || String(value);
  if (key === "city_of_birth_id") return resolveRegionName(value, regions, locale);
  return String(value);
}

function getFieldLabel(key: string, t: TFunction): string {
  const i18nKey = FIELD_LABELS[key];
  if (i18nKey) return t(i18nKey);
  return key;
}

function getSectionLabel(section: string, t: TFunction): string {
  const i18nKey = SECTION_LABELS[section];
  if (i18nKey) return t(i18nKey);
  return section;
}

function getSectionFilesEntries(
  section: string,
  uploadedFilesUrls: Record<string, string | null>,
): [string, string | null][] {
  return Object.entries(uploadedFilesUrls).filter(([key]) => key.startsWith(section));
}

function renderArraySection(
  sectionKey: string,
  requestedArray: unknown[],
  previousArray: unknown[],
  t: TFunction,
  tCommon: (key: string) => string,
  tApply: (key: string) => string,
  regions: RegionResponse[],
  locale: Locale,
) {
  return (
    <div className="flex flex-col gap-3">
      <span className="text-label-sm content-base-secondary">
        {t("personalSheet.diff.entriesCount", { count: requestedArray.length })}
      </span>
      {requestedArray.map(function renderArrayEntry(entry, idx) {
        const previousEntry = previousArray[idx];
        const entryFields =
          typeof entry === "object" && entry !== null ? Object.entries(entry as Record<string, unknown>) : [];
        return (
          <div key={idx} className="flex flex-col gap-1 surface-secondary-fill radius-sm p-3">
            {entryFields.map(function renderEntryField([fieldKey, fieldValue]) {
              const previousFieldValue =
                typeof previousEntry === "object" && previousEntry !== null
                  ? (previousEntry as Record<string, unknown>)[fieldKey]
                  : undefined;
              const changed = String(previousFieldValue) !== String(fieldValue);
              return (
                <div key={fieldKey} className="grid grid-cols-[1fr_1fr_1fr] gap-x-4 py-1.5 items-start">
                  <span className="text-body-regular-sm content-action-neutral">{getFieldLabel(fieldKey, t)}</span>
                  <span className="text-body-regular-sm content-base-primary">
                    {formatValue(previousFieldValue, fieldKey, t, tCommon, tApply, "", regions, locale)}
                  </span>
                  <span
                    className={
                      changed ? "text-body-bold-sm content-base-primary" : "text-body-regular-sm content-base-primary"
                    }>
                    {formatValue(fieldValue, fieldKey, t, tCommon, tApply, "", regions, locale)}
                  </span>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

function renderObjectSection(
  sectionKey: string,
  requestedObj: Record<string, unknown>,
  previousObj: Record<string, unknown>,
  t: TFunction,
  tCommon: (key: string) => string,
  tApply: (key: string) => string,
  regions: RegionResponse[],
  locale: Locale,
) {
  const gender = String(requestedObj.gender ?? previousObj.gender ?? "");
  const fieldEntries = Object.entries(requestedObj);
  return (
    <div className="flex flex-col">
      <div className="grid grid-cols-[1fr_1fr_1fr] gap-x-4 text-body-bold-sm content-base-secondary pb-2 border-b surface-base-stroke">
        <span>{t("personalSheet.diff.field")}</span>
        <span>{t("personalSheet.diff.previous")}</span>
        <span>{t("personalSheet.diff.requested")}</span>
      </div>
      {fieldEntries.map(function renderFieldRow([fieldKey, requestedValue], index) {
        const previousValue = previousObj[fieldKey];
        const changed = String(previousValue) !== String(requestedValue);
        return (
          <div
            key={fieldKey}
            className={`grid grid-cols-[1fr_1fr_1fr] gap-x-4 py-2.5 items-center${index < fieldEntries.length - 1 ? " border-b surface-base-stroke" : ""}`}>
            <span className="text-body-regular-sm content-action-neutral">{getFieldLabel(fieldKey, t)}</span>
            <span className="text-body-regular-sm content-base-primary">
              {formatValue(previousValue, fieldKey, t, tCommon, tApply, gender, regions, locale)}
            </span>
            <span
              className={
                changed ? "text-body-bold-sm content-base-primary" : "text-body-regular-sm content-base-primary"
              }>
              {formatValue(requestedValue, fieldKey, t, tCommon, tApply, gender, regions, locale)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export default function SectionDiffRenderer({
  previousValues,
  requestedChanges,
  changedSections,
  uploadedFilesUrls = {},
}: Props) {
  const { t, i18n } = useTranslation("EmployeePortal");
  const { t: tCommon } = useTranslation("Common");
  const { t: tApply } = useTranslation("ApplyApplicationPage");
  const locale = (i18n.language as Locale) || "ru";
  const { data: regions = [] } = useGetRegionsQuery();

  return (
    <div className="flex flex-col gap-6">
      {changedSections.map(function renderSection(section) {
        const requestedSectionData = requestedChanges[section];
        const previousSectionData = previousValues[section];
        const sectionFiles = getSectionFilesEntries(section, uploadedFilesUrls);

        return (
          <div key={section} className="flex flex-col gap-3">
            <h3 className="text-heading-sm content-base-primary">{getSectionLabel(section, t)}</h3>

            {Array.isArray(requestedSectionData)
              ? renderArraySection(
                  section,
                  requestedSectionData,
                  Array.isArray(previousSectionData) ? previousSectionData : [],
                  t,
                  tCommon,
                  tApply,
                  regions,
                  locale,
                )
              : requestedSectionData !== null && typeof requestedSectionData === "object"
                ? renderObjectSection(
                    section,
                    requestedSectionData as Record<string, unknown>,
                    previousSectionData !== null && typeof previousSectionData === "object"
                      ? (previousSectionData as Record<string, unknown>)
                      : {},
                    t,
                    tCommon,
                    tApply,
                    regions,
                    locale,
                  )
                : null}

            {sectionFiles.length > 0 && (
              <div className="flex flex-col gap-1.5 mt-1">
                {sectionFiles.map(function renderFileEntry([fileKey, fileUrl]) {
                  return fileUrl ? (
                    <a
                      key={fileKey}
                      href={fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-body-regular-sm content-action-brand hover:underline">
                      {t("personalSheet.diff.viewFile")}
                    </a>
                  ) : (
                    <span key={fileKey} className="text-body-regular-sm content-base-secondary">
                      {t("personalSheet.diff.fileAttached")}
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
